import { getTabSessionUserKey } from '../utils/tabSessionUtils';

const STORAGE_KEY = 'workloop_active_tab_session';
const CLOSE_SIGNAL_KEY = 'workloop_close_tab_signal';
const CHANNEL_NAME = 'workloop_tab_session';
const HEARTBEAT_MS = 2000;
const STALE_LOCK_MS = 8000;
const CLOSE_CLAIM_DELAY_MS = 200;

export const SESSION_CLOSED_PATH = '/session-closed';

const isElectronShell = () => {
  if (typeof window === 'undefined') return false;
  if (window.workloopRealm) return true;
  try {
    return Boolean(window.process?.versions?.electron);
  } catch {
    return false;
  }
};

/** HashRouter (Electron) stores the route in the hash, not pathname. */
const getRoutePath = () => {
  if (typeof window === 'undefined') return '';
  const hash = window.location.hash || '';
  if (hash.startsWith('#/')) {
    return hash.slice(1).split('?')[0] || '/';
  }
  return window.location.pathname || '';
};

function isPublicAuthRoute(path) {
  return path === '/call'
    || path.startsWith('/call/')
    || path === '/attendance/analytics'
    || path.startsWith('/attendance/analytics/')
    || path === '/register'
    || path === '/login'
    || path.startsWith('/password-reset');
}

function isSatelliteSurface() {
  if (typeof window !== 'undefined') {
    try {
      if (window.workloopRealm?.isCallWindow?.()) return true;
    } catch {
      // ignore
    }
  }
  return isPublicAuthRoute(getRoutePath());
}

class TabSessionService {
  constructor() {
    this.tabId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    this.userId = null;
    this.isLeader = false;
    this.isSuperseded = false;
    this.showSessionConflict = false;
    this.listeners = new Set();
    this.heartbeatTimer = null;
    this.channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;

    this.handleStorage = this.handleStorage.bind(this);
    this.handleChannelMessage = this.handleChannelMessage.bind(this);
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
  }

  subscribe(listener) {
    this.listeners.add(listener);
    listener(this.getState());
    return () => this.listeners.delete(listener);
  }

  getState() {
    return {
      isLeader: this.isLeader,
      isSuperseded: this.isSuperseded,
      showSessionConflict: this.showSessionConflict,
    };
  }

  notify() {
    const state = this.getState();
    this.listeners.forEach((listener) => listener(state));
  }

  readSession() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  writeSession() {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        tabId: this.tabId,
        userId: this.userId,
        claimedAt: Date.now(),
      }),
    );
  }

  broadcast(message) {
    try {
      this.channel?.postMessage(message);
    } catch (error) {
      console.warn('Tab session broadcast failed:', error);
    }
  }

  init(userId) {
    if (!userId) return;

    // Call / analytics windows share localStorage with chat. Never claim
    // the single-session lock or the main window gets kicked out.
    if (isSatelliteSurface()) {
      this.userId = userId;
      this.isLeader = false;
      this.isSuperseded = false;
      this.showSessionConflict = false;
      this.notify();
      return;
    }

    const isSameUser = this.userId === userId;
    this.userId = userId;

    if (!isSameUser) {
      this.isLeader = false;
      this.isSuperseded = false;
      this.showSessionConflict = false;
    }

    window.addEventListener('storage', this.handleStorage);
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    if (this.channel) {
      this.channel.addEventListener('message', this.handleChannelMessage);
    }

    const existing = this.readSession();
    const hadOtherActiveTab = Boolean(
      existing
      && existing.tabId !== this.tabId
      && existing.userId === userId,
    );
    const lockIsStale = !existing?.claimedAt
      || (Date.now() - existing.claimedAt) > STALE_LOCK_MS;

    // Desktop is a single window: a leftover lock is always from a previous process.
    // Browser: only prompt when another tab is still heartbeating.
    if (hadOtherActiveTab && !isElectronShell() && !lockIsStale) {
      this.showSessionConflict = true;
      this.isLeader = false;
      this.isSuperseded = false;
      this.notify();
      return;
    }

    this.claimSession();
  }

  destroy(options = {}) {
    const { releaseLock = true } = options;
    this.stopHeartbeat();

    window.removeEventListener('storage', this.handleStorage);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    if (this.channel) {
      this.channel.removeEventListener('message', this.handleChannelMessage);
    }

    if (releaseLock) {
      const current = this.readSession();
      if (current?.tabId === this.tabId) {
        localStorage.removeItem(STORAGE_KEY);
      }
    }

    this.userId = null;
    this.isLeader = false;
    this.isSuperseded = false;
    this.showSessionConflict = false;
    this.notify();
  }

  claimSession(options = {}) {
    const { notifyOtherTabs = true } = options;
    if (!this.userId || isSatelliteSurface()) return;

    this.writeSession();
    this.isLeader = true;
    this.isSuperseded = false;
    this.showSessionConflict = false;

    if (notifyOtherTabs) {
      this.broadcast({
        type: 'TAKEOVER',
        tabId: this.tabId,
        userId: this.userId,
      });
    }

    this.startHeartbeat();
    this.notify();
  }

  useWorkloopHere() {
    this.claimSession();
  }

  closeOtherSession() {
    if (!this.userId) return;

    const closeSignal = {
      type: 'CLOSE_OTHER_TABS',
      tabId: this.tabId,
      userId: this.userId,
      at: Date.now(),
    };

    this.broadcast(closeSignal);
    try {
      localStorage.setItem(CLOSE_SIGNAL_KEY, JSON.stringify(closeSignal));
    } catch (error) {
      console.warn('Tab close signal storage failed:', error);
    }

    setTimeout(() => {
      this.claimSession({ notifyOtherTabs: false });
    }, CLOSE_CLAIM_DELAY_MS);
  }

  reclaimSession() {
    this.claimSession();
  }

  attemptCloseTab() {
    this.stopHeartbeat();
    this.isLeader = false;
    this.isSuperseded = false;
    this.showSessionConflict = false;

    const current = this.readSession();
    if (current?.tabId === this.tabId) {
      localStorage.removeItem(STORAGE_KEY);
    }

    this.navigateToSessionClosedPage();
  }

  navigateToSessionClosedPage() {
    if (window.location.pathname === SESSION_CLOSED_PATH) {
      return;
    }

    try {
      window.close();
    } catch {
      // Browsers block closing user-opened tabs.
    }

    window.location.replace(SESSION_CLOSED_PATH);
  }

  becomeSuperseded() {
    if (this.isSuperseded) return;

    this.isLeader = false;
    this.isSuperseded = true;
    this.showSessionConflict = false;
    this.stopHeartbeat();
    this.notify();
  }

  handleStorage(event) {
    if (event.key === CLOSE_SIGNAL_KEY) {
      this.handleCloseSignal(event.newValue);
      return;
    }
    if (event.key !== STORAGE_KEY) return;
    this.evaluateLeadership();
  }

  handleCloseSignal(rawValue) {
    if (!rawValue || !this.userId) return;

    try {
      const signal = JSON.parse(rawValue);
      if (
        signal.type === 'CLOSE_OTHER_TABS'
        && signal.userId === this.userId
        && signal.tabId !== this.tabId
      ) {
        this.attemptCloseTab();
      }
    } catch {
      // ignore malformed signal
    }
  }

  handleChannelMessage(event) {
    const { type, tabId, userId } = event.data || {};
    if (userId !== this.userId || tabId === this.tabId) {
      return;
    }

    if (type === 'TAKEOVER') {
      this.becomeSuperseded();
      return;
    }

    if (type === 'CLOSE_OTHER_TABS') {
      this.attemptCloseTab();
    }
  }

  evaluateLeadership() {
    const session = this.readSession();
    if (!session || session.userId !== this.userId) {
      return;
    }

    if (session.tabId === this.tabId) {
      if (!this.isLeader || this.isSuperseded) {
        this.isLeader = true;
        this.isSuperseded = false;
        this.showSessionConflict = false;
        this.startHeartbeat();
        this.notify();
      }
      return;
    }

    if (!this.showSessionConflict) {
      this.becomeSuperseded();
    }
  }

  handleBeforeUnload() {
    if (!this.isLeader) return;
    const current = this.readSession();
    if (current?.tabId === this.tabId) {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (!this.isLeader || !this.userId) return;

      const session = this.readSession();
      if (session?.tabId !== this.tabId) {
        this.becomeSuperseded();
        return;
      }

      this.writeSession();
    }, HEARTBEAT_MS);
  }

  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  isActiveTab() {
    return this.isLeader && !this.isSuperseded && !this.showSessionConflict;
  }

  shouldBlockRequests() {
    // Satellite / public auth UIs — keep API access without claiming the session lock
    if (isSatelliteSurface()) {
      return false;
    }
    if (!localStorage.getItem('token')) {
      return false;
    }
    // Don't block bootstrap (/auth/me) before the tab lock is claimed.
    if (!this.userId) {
      return false;
    }
    return !this.isActiveTab();
  }
}

export const bootstrapTabSession = () => {
  // Don't claim the single-session lock from satellite / public auth tabs
  if (isSatelliteSurface()) {
    return;
  }
  const sessionUserKey = getTabSessionUserKey();
  if (!sessionUserKey) return;
  tabSessionService.init(sessionUserKey);
};

const tabSessionService = new TabSessionService();
export default tabSessionService;
